/**
 * Voice Memo Components
 * 
 * Audio recording and playback components for Team Feed posts.
 * - VoiceMemoPlayer: Plays back recorded voice memos with waveform visualization
 * - VoiceMemoButton: Records voice memos with timer and permission handling
 */

import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import { Play, Pause, Mic, MicOff } from 'lucide-react';
import UploadProgress from '@/components/shared/UploadProgress';
import { uploadWithProgress } from '@/lib/uploadVideoWithProgress';

/**
 * Voice Memo Player Component
 * 
 * Displays an audio player with:
 * - Play/pause controls
 * - Waveform-style progress visualization
 * - Playback speed control (1x, 1.5x, 2x)
 * - Time display with server-provided duration fallback for mobile
 * - Supports both URL-based audio (new) and base64-encoded audio (legacy)
 * 
 * @param {Object} props
 * @param {string} props.audioData - URL or base64 encoded audio data
 * @param {string} props.mimeType - MIME type of the audio
 * @param {number} props.serverDuration - Server-provided duration in seconds (fallback for mobile)
 */
export function VoiceMemoPlayer({ audioData, mimeType, serverDuration }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [rawDuration, setRawDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const audioRef = useRef(null);
  const progressRef = useRef(null);

  // Use browser-reported duration when finite; fall back to server-provided duration for mobile
  const duration = (isFinite(rawDuration) && rawDuration > 0) ? rawDuration : (serverDuration || 0);

  // Detect if audioData is a URL or base64
  const isUrl = audioData && (audioData.startsWith('http://') || audioData.startsWith('https://'));
  const primaryMime = mimeType || 'audio/mp4';
  const fallbackMime = primaryMime.includes('webm') ? 'audio/mp4' : 'audio/webm';
  const audioSrc = isUrl ? audioData : `data:${primaryMime};base64,${audioData}`;

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  const togglePlay = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  // Shared handler for both loadedmetadata and durationchange events.
  // Only updates if browser reports a valid finite duration (filters out Infinity on mobile).
  const handleDurationAvailable = () => {
    if (audioRef.current && isFinite(audioRef.current.duration) && audioRef.current.duration > 0) {
      setRawDuration(audioRef.current.duration);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
    }
  };

  const handleProgressClick = (e) => {
    if (!progressRef.current || !audioRef.current || duration <= 0) return;
    const rect = progressRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * duration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const formatTime = (seconds) => {
    if (!isFinite(seconds) || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = Math.min(duration > 0 ? (currentTime / duration) * 100 : 0, 100);

  const cyclePlaybackRate = () => {
    const rates = [1, 1.5, 2];
    const currentIndex = rates.indexOf(playbackRate);
    const nextIndex = (currentIndex + 1) % rates.length;
    setPlaybackRate(rates[nextIndex]);
  };

  return (
    <div className="mb-3 p-4 bg-gradient-to-r from-slate-50 dark:from-[#1e1f22] to-slate-100 dark:to-[#2d2f33] rounded-xl border border-slate-200 dark:border-white/10">
      <audio
        ref={audioRef}
        src={audioSrc}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleDurationAvailable}
        onDurationChange={handleDurationAvailable}
        onEnded={handleEnded}
        onError={(e) => {
          // Try fallback format for legacy base64 recordings (old recordings may be webm, new ones are mp4)
          if (!isUrl && audioRef.current) {
            const currentSrc = audioRef.current.src;
            if (currentSrc.includes(primaryMime)) {
              audioRef.current.src = `data:${fallbackMime};base64,${audioData}`;
            }
          }
        }}
        preload="metadata"
      />
      
      <div className="flex items-center gap-4">
        {/* Play/Pause Button */}
        <button
          onClick={togglePlay}
          className="w-12 h-12 bg-blue-500 hover:bg-blue-600 rounded-full flex items-center justify-center text-white shadow-lg transition-all hover:scale-105 flex-shrink-0"
          data-testid="audio-play-btn"
        >
          {isPlaying ? (
            <Pause className="w-5 h-5" />
          ) : (
            <Play className="w-5 h-5 ml-0.5" />
          )}
        </button>

        {/* Progress & Controls */}
        <div className="flex-1 min-w-0">
          {/* Waveform-style progress bar */}
          <div 
            ref={progressRef}
            className="h-10 bg-slate-200 dark:bg-[#3e4145] rounded-lg cursor-pointer relative overflow-hidden"
            onClick={handleProgressClick}
          >
            {/* Progress fill */}
            <div 
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-400 to-blue-500 transition-all duration-100"
              style={{ width: `${progress}%` }}
            />
            {/* Waveform visualization (static bars) */}
            <div className="absolute inset-0 flex items-center justify-around px-1">
              {[...Array(30)].map((_, i) => {
                const height = 20 + Math.sin(i * 0.5) * 15 + Math.sin(i * 0.3) * 10;
                const isActive = (i / 30) * 100 <= progress;
                return (
                  <div
                    key={i}
                    className={`w-1 rounded-full transition-colors ${isActive ? 'bg-white/80 dark:bg-[#2d2f33]/80' : 'bg-slate-400/40'}`}
                    style={{ height: `${height}%` }}
                  />
                );
              })}
            </div>
          </div>
          
          {/* Time & Speed Controls */}
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
            <button
              onClick={cyclePlaybackRate}
              className="text-xs font-semibold text-slate-600 dark:text-slate-300 hover:text-blue-600 bg-slate-200 dark:bg-[#3e4145] hover:bg-slate-300 px-2 py-1 rounded-md transition-colors"
              data-testid="audio-speed-btn"
            >
              {playbackRate}x
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Voice Memo Recording Button
 * 
 * Features:
 * - Browser microphone access
 * - Recording timer (max 10 minutes)
 * - Automatic format detection (WebM/MP4)
 * - Uploads to Supabase via backend, returns URL
 * 
 * @param {Object} props
 * @param {Function} props.onVoiceMemo - Callback with (urlOrData, durationSeconds, mimeType)
 * @param {boolean} props.disabled - Whether recording is disabled
 * @param {string} props.userId - User ID for the upload
 */
export const VoiceMemoButton = React.forwardRef(function VoiceMemoButton({ onVoiceMemo, disabled, userId, label, ...triggerProps }, ref) {
  const [isRecording, setIsRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const mediaRecorder = useRef(null);
  const audioChunks = useRef([]);
  const timerInterval = useRef(null);
  const streamRef = useRef(null);
  const recordedMimeType = useRef('audio/mp4');
  const recordingStartTime = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const MAX_DURATION = 600; // 10 minutes (R15: matches mobile; 64 kbps keeps 10 min ≈ 4.8 MB, under the 7 MB server cap)

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerInterval.current) {
        clearInterval(timerInterval.current);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Auto-stop at max duration
  useEffect(() => {
    if (isRecording && seconds >= MAX_DURATION) {
      stopRecording();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seconds, isRecording]);

  const startRecording = async () => {
    if (disabled) {
      toast.info('A voice memo is already attached. Remove it first to record a new one.');
      return;
    }

    try {
      // Request microphone permission
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        } 
      });
      
      streamRef.current = stream;

      // Find best supported format with compression
      let options = { audioBitsPerSecond: 64000 };
      const formats = [
        'audio/mp4',                   // AAC — plays on iOS, Android, Chrome, Safari, Edge
        'audio/webm;codecs=opus',      // Fallback for Firefox
        'audio/webm',
        'audio/ogg;codecs=opus',
        ''
      ];
      
      for (const format of formats) {
        if (!format || MediaRecorder.isTypeSupported(format)) {
          if (format) options.mimeType = format;
          break;
        }
      }

      const recorder = new MediaRecorder(stream, options);
      mediaRecorder.current = recorder;
      audioChunks.current = [];
      recordedMimeType.current = recorder.mimeType || 'audio/mp4';

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunks.current.push(e.data);
        }
      };

      recorder.onstop = async () => {
        const blob = new Blob(audioChunks.current, { type: recorder.mimeType || 'audio/mp4' });
        const duration = Math.floor((Date.now() - recordingStartTime.current) / 1000);
        
        // Upload to backend → Supabase Storage with real XHR byte progress.
        setIsUploading(true);
        setUploadProgress(0);
        try {
          if (blob.size > 7 * 1024 * 1024) {
            toast.error('That voice memo is over the 7 MB limit. Try a shorter recording.');
            return;
          }
          const formData = new FormData();
          // Determine file extension from MIME type (strip codec params like ;codecs=opus)
          const baseMime = (recordedMimeType.current || 'audio/mp4').split(';')[0];
          const mimeToExt = { 'audio/mp4': 'mp4', 'audio/webm': 'webm', 'audio/ogg': 'ogg', 'audio/wav': 'wav' };
          const ext = mimeToExt[baseMime] || 'mp4';
          formData.append('files', blob, `voice_memo_${Date.now()}.${ext}`);
          
          // 10% reserved for server-side work (Supabase Storage write).
          const data = await uploadWithProgress(
            `${API_URL}/api/comms/v3/upload/voice?user_id=${userId}`,
            formData,
            setUploadProgress,
            10,
          );
          onVoiceMemo(data.url, duration, recordedMimeType.current);
        } catch (err) {
          console.error('Voice upload error:', err);
          toast.error(err?.message || 'Failed to upload voice memo. Please try again.');
        } finally {
          setIsUploading(false);
          setUploadProgress(0);
        }

        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      };

      // Start recording
      recorder.start(100);
      setIsRecording(true);
      setSeconds(0);
      recordingStartTime.current = Date.now();

      // Start timer
      timerInterval.current = setInterval(() => {
        setSeconds(s => s + 1);
      }, 1000);

    } catch (err) {
      console.error('Microphone error:', err);
      
      let message = 'Could not access microphone.';
      if (err.name === 'NotAllowedError') {
        message = 'Microphone permission denied. Please allow microphone access in your browser settings.';
      } else if (err.name === 'NotFoundError') {
        message = 'No microphone found. Please connect a microphone.';
      }
      toast.error(message);
    }
  };

  const stopRecording = () => {
    if (timerInterval.current) {
      clearInterval(timerInterval.current);
      timerInterval.current = null;
    }

    if (mediaRecorder.current && mediaRecorder.current.state !== 'inactive') {
      mediaRecorder.current.stop();
    }
    
    setIsRecording(false);
  };

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const handleButtonClick = () => {
    if (isUploading) return;
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  return (
    <button
      ref={ref}
      {...triggerProps}
      type="button"
      onClick={handleButtonClick}
      disabled={(disabled && !isRecording) || isUploading}
      style={{ 
        cursor: (disabled && !isRecording) || isUploading ? 'not-allowed' : 'pointer',
        position: 'relative',
        zIndex: 10
      }}
      className={`
        inline-flex items-center justify-center h-8 px-2 rounded-md text-sm font-medium
        transition-all duration-200
        ${isRecording 
          ? 'bg-red-500 text-white shadow-lg' 
          : isUploading
            ? 'text-blue-400'
            : disabled 
              ? 'text-slate-300' 
              : 'text-slate-500 dark:text-slate-400 hover:text-blue-600 hover:bg-slate-100 dark:hover:bg-white/10'
        }
      `}
      data-testid="voice-memo-btn"
    >
      {isUploading ? (
        <div className="flex items-center gap-1.5">
          <Mic className="w-4 h-4" />
          <UploadProgress progress={uploadProgress} size={14} className="text-blue-500" />
        </div>
      ) : isRecording ? (
        <div className="flex items-center gap-1.5">
          <MicOff className="w-4 h-4" />
          <span className="font-mono text-xs">{formatTime(seconds)}</span>
          <span className="w-2 h-2 bg-card rounded-full animate-pulse" />
        </div>
      ) : (
        <><Mic className="w-4 h-4" />{label ? <span>{label}</span> : null}</>
      )}
    </button>
  );
});
